import React, { useState, useEffect } from 'react';
import { Download, Filter, Calendar, User, Building, X, FileText, Settings } from 'lucide-react';
import { commissionsService } from '../../services';
import agentsService from '../../services/agentsService';

const CommissionExportModal = ({ isOpen, onClose, currentFilters, searchTerm }) => {
  const [exportConfig, setExportConfig] = useState({
    status: currentFilters?.status || 'all',
    agentId: currentFilters?.agent || 'all',
    supplier: 'all',
    dateRange: currentFilters?.dateRange || 'all',
    startDate: '',
    endDate: '',
    search: searchTerm || '',
    fields: [
      'id', 'agent_name', 'status', 'reservation_number',
      'guest_first_name', 'guest_last_name', 'supplier',
      'booking_date', 'travel_date', 'gross_commission',
      'agent_commission', 'created_at'
    ]
  });
  
  const [agents, setAgents] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('filters');

  // Available fields for export organized by category
  const availableFields = {
    basic: [
      { key: 'id', label: 'Commission ID' },
      { key: 'agent_name', label: 'Agent Name' },
      { key: 'agent_email', label: 'Agent Email' },
      { key: 'agency_name', label: 'Agency Name' },
      { key: 'status', label: 'Status' }
    ],
    booking: [
      { key: 'reservation_number', label: 'Reservation Number' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'booking_date', label: 'Booking Date' },
      { key: 'travel_date', label: 'Travel Date' }
    ],
    guest: [
      { key: 'guest_first_name', label: 'Guest First Name' },
      { key: 'guest_last_name', label: 'Guest Last Name' }
    ],
    agent_info: [
      { key: 'first_name', label: 'Agent First Name' },
      { key: 'last_name', label: 'Agent Last Name' },
      { key: 'email', label: 'Agent Contact Email' },
      { key: 'phone', label: 'Agent Phone' }
    ],
    financial: [
      { key: 'gross_commission', label: 'Gross Commission' },
      { key: 'agent_commission', label: 'Agent Commission' }
    ],
    dates: [
      { key: 'created_at', label: 'Submitted Date' },
      { key: 'updated_at', label: 'Last Updated' },
      { key: 'processed_at', label: 'Processed Date' }
    ],
    notes: [
      { key: 'notes', label: 'Agent Notes' },
      { key: 'admin_notes', label: 'Admin Notes' }
    ]
  };

  // Load agents and suppliers on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [agentData, supplierData] = await Promise.all([
          agentsService.getAll(),
          commissionsService.getSuppliers()
        ]);
        setAgents(agentData || []);
        setSuppliers(supplierData || []);
      } catch (error) {
        console.error('Failed to load agents and suppliers:', error);
      }
    };
    
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Get preview count when filters change
  useEffect(() => {
    if (isOpen && activeTab === 'filters') {
      getPreviewCount();
    }
  }, [exportConfig, isOpen, activeTab]);

  const getPreviewCount = async () => {
    setIsLoadingPreview(true);
    try {
      // This would ideally be a separate endpoint that just returns count
      // For now, we'll use the existing endpoint but only get the count from headers
      const params = {
        ...exportConfig,
        fields: 'id' // Only get ID to minimize data transfer
      };
      
      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const response = await commissionsService.exportGeneral(params);
      
      // Count lines in CSV (subtract 1 for header)
      const lines = response.split('\n').filter(line => line.trim() !== '');
      setPreviewCount(Math.max(0, lines.length - 1));
    } catch (error) {
      console.error('Failed to get preview count:', error);
      setPreviewCount(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleFieldToggle = (fieldKey) => {
    setExportConfig(prev => ({
      ...prev,
      fields: prev.fields.includes(fieldKey)
        ? prev.fields.filter(f => f !== fieldKey)
        : [...prev.fields, fieldKey]
    }));
  };

  const handleSelectAllFields = (category) => {
    const categoryFields = availableFields[category].map(f => f.key);
    setExportConfig(prev => ({
      ...prev,
      fields: [...new Set([...prev.fields, ...categoryFields])]
    }));
  };

  const handleDeselectAllFields = (category) => {
    const categoryFields = availableFields[category].map(f => f.key);
    setExportConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => !categoryFields.includes(f))
    }));
  };

  const handleExport = async () => {
    if (exportConfig.fields.length === 0) {
      alert('Please select at least one field to export.');
      return;
    }

    setIsExporting(true);
    try {
      const params = {
        ...exportConfig,
        fields: exportConfig.fields.join(',')
      };
      
      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      console.log('Exporting with params:', params);
      
      const response = await commissionsService.exportGeneral(params);
      
      // Check if response is an error message (text/plain) instead of CSV
      if (response instanceof Blob) {
        const text = await response.text();
        if (text.startsWith('Export failed:')) {
          throw new Error(text);
        }
        
        // Create download
        const blob = new Blob([text], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.download = `commissions_export_${timestamp}.csv`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        onClose();
      } else {
        // Fallback for non-blob responses
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.download = `commissions_export_${timestamp}.csv`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        onClose();
      }
    } catch (error) {
      console.error('Export failed:', error);
      
      // Try to extract detailed error message from blob response
      let errorMessage = 'Export failed. Please try again.';
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          if (text.startsWith('Export failed:')) {
            errorMessage = text;
          } else if (text) {
            errorMessage = `Export failed: ${text}`;
          }
        } catch (e) {
          console.error('Failed to parse error blob:', e);
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'No commissions found matching the specified criteria.';
      }
      
      alert(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Download className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Export Commissions</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('filters')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'filters'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Filter className="w-4 h-4 inline mr-2" />
            Filters & Preview
          </button>
          <button
            onClick={() => setActiveTab('fields')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'fields'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Field Selection ({exportConfig.fields.length})
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'filters' && (
            <div className="space-y-6">
              {/* Preview Count */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-900">Export Preview</span>
                  </div>
                  <div className="text-blue-700">
                    {isLoadingPreview ? (
                      <span className="animate-pulse">Loading...</span>
                    ) : previewCount !== null ? (
                      <span className="font-semibold">{previewCount} commissions</span>
                    ) : (
                      <span className="text-gray-500">Unable to preview</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={exportConfig.status}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="processing">Processing</option>
                    <option value="denied">Denied</option>
                    <option value="on_hold">On Hold</option>
                    <option value="needs_reply">Needs Reply</option>
                    <option value="needs_admin_reply">Needs Admin Reply</option>
                    <option value="needs_agent_reply">Needs Agent Reply</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>

                {/* Agent Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent
                  </label>
                  <select
                    value={exportConfig.agentId}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, agentId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Agents</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name || `${agent.first_name} ${agent.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <select
                    value={exportConfig.dateRange}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, dateRange: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                    <option value="custom">Custom Date Range</option>
                  </select>
                </div>

                {/* Supplier Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier
                  </label>
                  <select
                    value={exportConfig.supplier}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, supplier: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Suppliers</option>
                    {suppliers.map(supplier => (
                      <option key={supplier} value={supplier}>
                        {supplier}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Custom Date Range */}
              {exportConfig.dateRange === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportConfig.startDate}
                      onChange={(e) => setExportConfig(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportConfig.endDate}
                      onChange={(e) => setExportConfig(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Search Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={exportConfig.search}
                  onChange={(e) => setExportConfig(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search reservations, guests, suppliers, agents..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-6">
              {Object.entries(availableFields).map(([category, fields]) => (
                <div key={category} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 capitalize">
                      {category.replace('_', ' ')} Fields
                    </h3>
                    <div className="space-x-2">
                      <button
                        onClick={() => handleSelectAllFields(category)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => handleDeselectAllFields(category)}
                        className="text-xs text-gray-600 hover:text-gray-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {fields.map(field => (
                      <label key={field.key} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={exportConfig.fields.includes(field.key)}
                          onChange={() => handleFieldToggle(field.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {exportConfig.fields.length} fields selected
            {previewCount !== null && ` • ${previewCount} commissions to export`}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || exportConfig.fields.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionExportModal;
